import { GetStaticPaths, GetStaticProps } from "next";
import {
  EditFormProps,
  GiftSubscribeCard,
  giftSubscribeCardKeyBuilder,
  OverlapPreview,
  SuperHero,
  TabbedProductPreview,
} from "@bluebottlecoffee/design-system/components";
import { Address } from "@bluebottlecoffee/design-system/components/lib/types";
import { toHTML } from "@portabletext/to-html";
import { Fragment, useEffect, useMemo, useState } from "react";
import usePlacesAutocompleteService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { CanonicalHead } from "../../../../components/CanonicalHead";
import { ComponentIdInjector } from "../../../../lib/component-id-injector";
import useVariantPrice from "../../../../lib/chord/hooks/components/use-variant-price";
import {
  GiftSubscriptionCheckoutForm,
  GiftSubscriptionCheckoutFormProps,
} from "../../../../components/gift-subscription-checkout-form/GiftSubscriptionCheckoutForm";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  formatAddressSearchString,
  handleSelectAddressPrediction,
} from "../../../../lib/component-utils/account-addresses";
import renderModule from "../../../../lib/render-module";
import sanityClient from "../../../../lib/sanity-client";
import {
  LocalizedResultWithCopy,
  LocalizedSanityResult,
  getLocalizedResultWithCopy as translationsToCopy,
  localizedQuery,
  buildGiftableSubscribableProductsQuery,
  RevampGiftModule,
  DereferencedGiftSubsPreviewTabs,
} from "../../../../lib/sanity/gift-subscription-queries";
import { experiments, useExperiment } from "../../../../lib/ABTesting";
import { BaseProps } from "../../../../lib/sanity/shared";
import {
  ConversionSchema,
  toProductProps,
} from "../../../../lib/transformers/conversion";
import { toGiftSubscribeCardProps } from "../../../../lib/transformers/gift-subscribe-card";
import {
  toGiftSubsOverlapPreviewProps,
  toGiftSubsTabbedPreviewTabs,
} from "../../../../lib/transformers/gift-subscriptions-module";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import {
  getDefaultStaticPathsMiniCart,
  getDefaultStaticProps,
} from "../../../../lib/utils";
import { Dialect } from "../../../../lib/utils/locale";
import { resolveTabbedCollections } from "../../../../lib/resolve-tabbed-collections";

export interface GiftSubscriptionsPageData extends BaseProps {
  pageProps: LocalizedResultWithCopy & {
    pageProducts: ConversionSchema[];
  };
}

async function getSanityData(lang: string): Promise<LocalizedResultWithCopy> {
  const query = localizedQuery(lang);

  const localizedResult =
    await sanityClient().fetch<LocalizedSanityResult>(query);

  return translationsToCopy({
    localizedResult,
    lang,
  });
}

/**
 * Sanity Query
 * @param lang
 * @returns all products that are forSale, subscribable, and giftable
 */
async function getGiftableSubProducts(
  lang: Dialect["lang"],
): Promise<ConversionSchema[]> {
  const query = buildGiftableSubscribableProductsQuery(lang);
  const result = await sanityClient().fetch<ConversionSchema[]>(query);
  return result;
}

function validate(
  data: LocalizedResultWithCopy & {
    pageProducts: ConversionSchema[];
  },
) {
  const nonGiftableProduct = data.pageProducts.find(
    (product) => !product.isGiftable,
  );
  const notForSaleProduct = data.pageProducts.find(
    (product) => !product.forSale,
  );
  const productWithNoGiftableSubOptions = data.pageProducts.find(
    (product) => !product.subscriptionOptions?.some((so) => so?.isGiftable),
  );
  if (!data.title) {
    throw Error("Page must have a title.");
  }
  if (data.pageProducts.length < 3) {
    throw Error(
      "Page requires at least 3 valid subscribable, giftable products.",
    );
  }
  if (nonGiftableProduct) {
    throw Error(
      `Page contains ${nonGiftableProduct.name.eng}, a product that is not` +
        ` giftable. Remove ${nonGiftableProduct.name.eng} from page or mark` +
        ` it giftable.`,
    );
  }
  if (notForSaleProduct) {
    throw Error(
      `Page contains ${notForSaleProduct.name.eng}, a product that is not` +
        ` for sale.Remove ${notForSaleProduct.name.eng} from page or mark it` +
        ` for sale.`,
    );
  }
  if (productWithNoGiftableSubOptions) {
    throw Error(
      `Page contains ${productWithNoGiftableSubOptions.name.eng} which has` +
        ` no giftable subscription options. Please mark at least one` +
        ` subscription option giftable or mark product not giftable`,
    );
  }
}

export const getStaticProps: GetStaticProps<GiftSubscriptionsPageData> = async (
  context,
) => {
  const { props: defaultProps } = await getDefaultStaticProps(context);

  const { lang, region } = defaultProps;

  const sanityData = await getSanityData(lang);
  const { subscribableProductPreview } = sanityData;
  // All products in the CMS with subscription options that are giftable
  const subscribableProducts = await getGiftableSubProducts(lang);
  // Filter out the products selected in the page schema
  const subscribableProductsSansPreview = subscribableProducts.filter(
    (product) =>
      !subscribableProductPreview
        .map((previewProduct) => previewProduct.name[lang])
        .includes(product.name[lang]),
  );
  // If renderAllGiftableSubs is true, concatenate subscribable products to
  // product list in page schema. If false, only return products in page schema.
  const pageProducts = sanityData.renderAllGiftableSubs
    ? subscribableProductPreview.concat(subscribableProductsSansPreview)
    : subscribableProductPreview;

  const pageProps = { ...sanityData, pageProducts };
  validate(pageProps);

  await resolveTabbedCollections(
    [sanityData.modules, sanityData.revampGiftModules],
    { lang, region },
  );

  return {
    props: {
      pageProps,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPathsMiniCart;

const GiftSubscriptions = ({
  pageProps,
  ...layoutWrapperProps
}: GiftSubscriptionsPageData) => {
  const { region, lang, appPlayLinks, aria } = layoutWrapperProps;
  const {
    liveRegionLoadingText,
    isRequired,
    inputTooLong,
    emailFormatInvalid,
    usPostalCodeFormatInvalid,
    countryCodeFormatInvalid,
    giftNoteTooLong,
  } = aria;
  const {
    renderGiftSubscribeModule,
    renderStartDate,
    title,
    hero,
    editFormNote,
    editFormTitleCreate,
    giftSubDetailsModal,
    giftSubRecipientAddressModal,
    giftSubReviewOrderModal,
    giftSubscriptionsCopy,
    modules,
    orderCopy,
    recipientAddressCopy,
    sizeAndScheduleCopy,
    pageProducts,
    subscriptionCollectionsCopy,
    viewMoreCtaText,
    revampGiftModules,
  } = pageProps;

  const isGiftRevampVariant =
    useExperiment<string>(experiments.giftSubscriptionsRevamp, "control") ===
    "variant";

  const { back, ...giftSubDetailsModalCopy } = giftSubscriptionsCopy;
  const { immediately } = orderCopy;

  const checkoutFormCopy: GiftSubscriptionCheckoutFormProps["copy"] = {
    back,
    immediately,
    liveRegionLoadingText,
    isRequired,
    inputTooLong,
    emailFormatInvalid,
    usPostalCodeFormatInvalid,
    countryCodeFormatInvalid,
    giftNoteTooLong,
  };

  // subscribableProductPreview cannot be filtered for giftable subscription
  // options so we use this function
  const giftableSubscriptionOptions = (product: ConversionSchema) =>
    product.subscriptionOptions.filter((subOption) => subOption.isGiftable);

  const { assortmentButtonTextBeforeCost: modalTriggerText } =
    subscriptionCollectionsCopy;

  const [addressQueries, setAddressQueries] = useState<
    EditFormProps["addressPredictions"]
  >([]);

  // use placePredictions rather than queryPredictions in order to utilize
  // componentRestrictions (restricts results to localized region, ie: "us")
  // https://developers.google.com/maps/documentation/javascript/reference/places-autocomplete-service#AutocompleteService-Methods
  const { placePredictions, placesService, getPlacePredictions } =
    usePlacesAutocompleteService({
      apiKey: process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY,
    });

  const [showPlacePredictions, setShowPlacePredictions] =
    useState<boolean>(false);

  const placePredictionsMemo = useMemo(
    () => JSON.stringify(placePredictions),
    [placePredictions],
  );

  useEffect(() => {
    const predictions = JSON.parse(placePredictionsMemo);
    if (predictions.length) {
      setShowPlacePredictions(true);
      setAddressQueries(predictions);
    } else {
      setShowPlacePredictions(false);
    }
  }, [placePredictionsMemo]);

  const handleAddressInput = (address: Address) => {
    getPlacePredictions({
      componentRestrictions: { country: region },
      input: formatAddressSearchString(address),
      language: lang,
      region,
    });
  };

  const getProductBagIcon = (quantity: number) => {
    switch (quantity) {
      case 2:
        return "productBags2";
      case 3:
        return "productBags3";
      case 1:
      default:
        return "productBags1";
    }
  };

  const modalContent = (product: ConversionSchema) => (
    <GiftSubscriptionCheckoutForm
      checkoutFlowVariant={isGiftRevampVariant ? "revamp" : "default"}
      copy={checkoutFormCopy}
      renderStartDate={renderStartDate}
      giftSubConfirmation={{
        copy: {
          header: toHTML(giftSubReviewOrderModal.heading[lang]),
          orderTranslations: orderCopy,
          tbd: giftSubscriptionsCopy.tbd,
        },
        ...(giftSubReviewOrderModal.disclaimer && {
          disclaimer: giftSubReviewOrderModal.disclaimer[lang],
        }),
        nextCTAText: giftSubReviewOrderModal.nextCtaText[lang],
        renderStartDate,
      }}
      giftSubDetails={{
        copy: {
          ...giftSubDetailsModalCopy,
          bagSizeHeader: isGiftRevampVariant
            ? "Select size"
            : sizeAndScheduleCopy.bagSizeHeader,
          deliverySchedule: isGiftRevampVariant
            ? "Delivery Schedule"
            : sizeAndScheduleCopy.deliveryScheduleHeader,
          subscriptionDuration: isGiftRevampVariant
            ? "Duration"
            : giftSubscriptionsCopy.subscriptionDuration,
          installments: isGiftRevampVariant
            ? "installments"
            : giftSubDetailsModalCopy.installments,
          startNow: isGiftRevampVariant
            ? "Start now"
            : giftSubDetailsModalCopy.startNow,
          startLater: isGiftRevampVariant
            ? "Start later"
            : giftSubDetailsModalCopy.startLater,
          freeShipping: orderCopy.freeShippingText,
          heading: toHTML(giftSubDetailsModal.heading[lang]),
          description: toHTML(giftSubDetailsModal.description?.[lang]),
        },
        renderStartDate,
        ...(giftSubDetailsModal.disclaimer && {
          disclaimer: giftSubDetailsModal.disclaimer[lang],
        }),
        nextCTAText: giftSubDetailsModal.nextCtaText[lang],
        product: {
          ...toProductProps(product, lang),
          subscriptionOptions: giftableSubscriptionOptions(product).map(
            (subOption) => {
              const { _key, quantityLabel, ...rest } = subOption;
              const label =
                quantityLabel.label && typeof quantityLabel.label === "object"
                  ? quantityLabel.label![lang]
                  : quantityLabel.label;

              return {
                ...rest,
                quantityLabel: {
                  ...quantityLabel,
                  label,
                  icon: getProductBagIcon(quantityLabel.quantity),
                },
                _id: _key,
              };
            },
          ),
        },
        useVariantPrice,
      }}
      giftSubRecipientAddress={{
        copy: {
          heading: toHTML(giftSubRecipientAddressModal.heading[lang]),
          description: toHTML(giftSubRecipientAddressModal.description?.[lang]),
          recipientEmailFormLabel: giftSubscriptionsCopy.recipientEmailAddress,
          giftNoteFormLabel: giftSubscriptionsCopy.giftNote,
          ...(isGiftRevampVariant && {
            giftNoteHelperText:
              "Note: the sender's name is added automatically. Avoid signing your note to prevent it appearing twice.",
          }),
        },
        ...(giftSubRecipientAddressModal.disclaimer && {
          disclaimer: giftSubRecipientAddressModal.disclaimer[lang],
        }),
        editFormProps: {
          address: {
            street: "",
            locality: "",
            district: "",
            postalCode: "",
            countryCode: "",
            default: false,
          },
          addressPredictions: showPlacePredictions ? addressQueries : [],
          onSelectAddressPrediction: (prediction) =>
            handleSelectAddressPrediction(prediction, placesService, () =>
              setShowPlacePredictions(false),
            ),
          copy: {
            ...recipientAddressCopy,
            editFormTitleCreate: editFormTitleCreate[lang],
            editFormNote: editFormNote?.[lang] && toHTML(editFormNote[lang]),
            ...(isGiftRevampVariant && {
              editFormNameFieldLabel: "Recipient's Full Name",
            }),
          },
          isStandaloneForm: false,
          onAddressInput: handleAddressInput,
          region: region as EditFormProps["region"],
        },
        nextCTAText: giftSubRecipientAddressModal.nextCtaText[lang],
      }}
      region={region}
      lang={lang}
    />
  );

  const buildProductCards = (
    products: ConversionSchema[],
    { includeReview = false }: { includeReview?: boolean } = {},
  ) =>
    products.map((product) => ({
      ...toGiftSubscribeCardProps({
        data: product,
        includeReview,
        lang,
        giftableSubscriptionOptions: giftableSubscriptionOptions(product),
        modalTriggerText,
      }),
      modalContent: modalContent(product),
      ...(isGiftRevampVariant && { modalDismissButtonFloating: true }),
    }));

  // products chosen in the CMS
  const productCards = buildProductCards(pageProducts);

  const renderRevampGiftModule = (module: RevampGiftModule) => {
    if (module._type === "giftSubsPreviewTabs") {
      const tabsModule = module as unknown as DereferencedGiftSubsPreviewTabs;
      const tabDrafts = (tabsModule.tabs ?? [])
        .filter((tab) =>
          Boolean(tab?._key && tab?.subscribableProducts?.length),
        )
        .map((tab) => ({
          id: tab._key,
          label: tab.label[lang],
          cards: buildProductCards(tab.subscribableProducts, {
            includeReview: true,
          }),
        }));

      const previewTabs = toGiftSubsTabbedPreviewTabs({
        tabs: tabDrafts,
        viewMoreCtaText: tabsModule.viewMoreCtaText[lang],
        dialect: { region, lang },
      });

      if (previewTabs.length === 0) {
        return null;
      }

      return (
        <ComponentIdInjector id={tabsModule.anchorId}>
          <TabbedProductPreview
            {...(tabsModule.heading && { heading: tabsModule.heading[lang] })}
            {...(tabsModule.subheading && {
              subheading: tabsModule.subheading[lang],
            })}
            Card={GiftSubscribeCard}
            keyBuilder={giftSubscribeCardKeyBuilder}
            tabs={previewTabs}
          />
        </ComponentIdInjector>
      );
    }

    return renderModule({ module, region, lang, appPlayLinks, aria });
  };

  return (
    <>
      <CanonicalHead
        region={region}
        lang={lang}
        slug="subscriptions/gift-subscriptions"
      />
      <LayoutWrapper
        {...{
          ...layoutWrapperProps,
          pageTitle: title,
        }}
        isTransparent={!isGiftRevampVariant}
      >
        {isGiftRevampVariant ? (
          <div className="giftSubscriptionsRevamp">
            {revampGiftModules?.map((module) =>
              module ? (
                <Fragment key={module._key}>
                  {renderRevampGiftModule(module)}
                </Fragment>
              ) : null,
            )}
          </div>
        ) : (
          <>
            {hero?.length > 0 && (
              <SuperHero {...toSuperHeroProps(hero, aria, region, lang)} />
            )}
            {renderGiftSubscribeModule && (
              <OverlapPreview
                {...toGiftSubsOverlapPreviewProps({
                  productCards,
                  viewMoreCtaText: viewMoreCtaText as unknown as string, // localized in the query
                  dialect: { region, lang },
                })}
              />
            )}
            {modules &&
              modules.length > 0 &&
              modules.map((module) => (
                <div key={module._key}>
                  {renderModule({
                    module,
                    region,
                    lang,
                    appPlayLinks,
                    aria,
                  })}
                </div>
              ))}
          </>
        )}
      </LayoutWrapper>
    </>
  );
};

export default GiftSubscriptions;
